// hooks/useEscapeKey.ts

import { useEffect, useRef } from 'react';

/**
 * Hook that calls a handler when the Escape key is pressed.
 *
 * @param onEscape - Callback invoked on Escape keydown.
 * @param isActive - Whether the listener is active. Defaults to true.
 *
 * @example
 * // Always listening
 * useEscapeKey(() => setIsOpen(false));
 *
 * @example
 * // Only when open
 * useEscapeKey(() => setIsOpen(false), isOpen);
 */
export function useEscapeKey(
  onEscape: () => void,
  isActive: boolean = true
): void {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscapeRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);
}
